'use client';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { useFormik } from 'formik';
import CountrySelect from './CountrySelect';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import ButtonLoader from './mui/ButtonLoader';
import * as Yup from 'yup';

interface CountryType {
  code: string;
  label: string;
  suggested?: boolean;
}

const validationSchema = Yup.object({
  country: Yup.object().nullable().required('Please select a country').shape({
    code: Yup.string().required(),
    label: Yup.string().required(),
  }),
});

interface Props {
  open: boolean;
  onClose: () => void;
  onSubmit: (country: CountryType) => Promise<void>;
}

export default function StripeSelectCountryDialog(props: Props) {
  const { open, onClose, onSubmit } = props;

  const formik = useFormik<{ country: CountryType | null }>({
    validationSchema,
    initialValues: {
      country: null,
    },
    onSubmit: (values) => onSubmit(values.country!).then(onClose),
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{
        mountOnEnter: true,
        unmountOnExit: true,
        onExited: () => formik.resetForm(),
      }}
    >
      <DialogContent>
        <Typography variant="h3" mb={1}>
          Choose country
        </Typography>
        <Typography variant="body2" color="secondary.low" mb={2}>
          Select where your business or work is based
        </Typography>

        <form onSubmit={formik.handleSubmit}>
          <CountrySelect
            required
            name="country"
            placeholder="Choose country"
            value={formik.values.country}
            onChange={(value) => formik.setFieldValue('country', value)}
            onBlur={formik.handleBlur}
            error={formik.touched.country && Boolean(formik.errors.country)}
            helperText={(formik.touched.country && (formik.errors.country as string)) || undefined}
          />
        </form>
        <Stack direction="row" justifyContent="flex-end" alignItems="center" mt={4} gap="10px">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" onClick={formik.submitForm}>
            <ButtonLoader label="Continue" loading={formik.isSubmitting} />
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
